import { useMemo, useRef, useState, useEffect } from 'react'
import { CheckIcon, ChevronDownIcon, EyeIcon, PlusIcon, SearchIcon } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { ModelMetaBadges, ModelProviderTag } from './ModelBadges'
import type { LitellmModel } from './types'

type Props = {
  catalog: LitellmModel[]
  selectedNames: string[]
  onPick: (modelName: string) => void
  disabled?: boolean
  label?: string
  align?: 'start' | 'center' | 'end'
  variant?: 'add' | 'replace'
  triggerClassName?: string
}

type DisplayModel = LitellmModel & {
  duplicateCount: number
  hiddenByDefault: boolean
}

const RECOMMENDED_ORION_MODEL = 'stationzero-orion-2.0'

function isRouteAlias(modelName: string) {
  return modelName.startsWith('stationzero-orion-route-')
}

function isLegacyOrionAlias(modelName: string) {
  return modelName === 'stationzero-orion'
}

function isLegacyOrionVariant(modelName: string) {
  return /^stationzero-orion-gpt-5\.[0-4](?:-|$)/.test(modelName)
}

function shouldHideByDefault(modelName: string, hasRecommendedOrion: boolean) {
  if (isRouteAlias(modelName)) return true
  if (!hasRecommendedOrion) return false
  return isLegacyOrionAlias(modelName) || isLegacyOrionVariant(modelName)
}

function metadataScore(model: LitellmModel) {
  let score = 0
  if (model.provider) score += 2
  if (model.mode) score += 1
  if (model.max_input_tokens) score += 1
  if (model.input_cost_per_million != null || model.output_cost_per_million != null) score += 2
  if (model.supports_function_calling) score += 1
  if (model.supports_vision) score += 1
  return score
}

function dedupeCatalog(catalog: LitellmModel[]) {
  const byName = new Map<string, DisplayModel>()
  catalog.forEach((model) => {
    const existing = byName.get(model.model_name)
    if (!existing) {
      byName.set(model.model_name, { ...model, duplicateCount: 1, hiddenByDefault: false })
      return
    }

    existing.duplicateCount += 1
    if (metadataScore(model) > metadataScore(existing)) {
      byName.set(model.model_name, {
        ...model,
        duplicateCount: existing.duplicateCount,
        hiddenByDefault: false,
      })
    }
  })
  return Array.from(byName.values())
}

function modelRank(modelName: string) {
  if (modelName === RECOMMENDED_ORION_MODEL) return 0
  if (modelName.startsWith('stationzero-orion-gpt-5.5')) return 10
  if (modelName === 'stationzero-1.5') return 20
  if (modelName.includes('claude-sonnet')) return 30
  if (modelName.startsWith('stationzero-orion')) return 40
  return 100
}

function searchMatches(model: LitellmModel, term: string) {
  if (!term) return true
  return (
    model.model_name.toLowerCase().includes(term) ||
    (model.provider || '').toLowerCase().includes(term) ||
    (model.mode || '').toLowerCase().includes(term)
  )
}

function sortModels(models: DisplayModel[], term: string) {
  return [...models].sort((a, b) => {
    const aExact = term && a.model_name.toLowerCase() === term
    const bExact = term && b.model_name.toLowerCase() === term
    if (aExact !== bExact) return aExact ? -1 : 1

    const aStarts = term && a.model_name.toLowerCase().startsWith(term)
    const bStarts = term && b.model_name.toLowerCase().startsWith(term)
    if (aStarts !== bStarts) return aStarts ? -1 : 1

    const rankDiff = modelRank(a.model_name) - modelRank(b.model_name)
    if (rankDiff !== 0) return rankDiff

    return a.model_name.localeCompare(b.model_name)
  })
}

// Searchable popover that lists every model from the live catalog plus its
// metadata badges. `selectedNames` are dimmed but still selectable when
// variant === "replace" (single-select dropdown); skipped entirely when
// variant === "add" (multi-select picker).
export function ModelPickerPopover({
  catalog,
  selectedNames,
  onPick,
  disabled = false,
  label,
  align = 'start',
  variant = 'add',
  triggerClassName,
}: Props) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [contentWidth, setContentWidth] = useState<number>()

  useEffect(() => {
    if (!open) {
      setQuery('')
      setShowAdvanced(false)
      return
    }
    if (triggerRef.current) setContentWidth(triggerRef.current.offsetWidth)
    const raf = window.requestAnimationFrame(() => inputRef.current?.focus())
    return () => window.cancelAnimationFrame(raf)
  }, [open])

  const selectedSet = useMemo(() => new Set(selectedNames), [selectedNames])

  const displayModels = useMemo(() => {
    const uniqueModels = dedupeCatalog(catalog)
    const hasRecommendedOrion = uniqueModels.some((model) => model.model_name === RECOMMENDED_ORION_MODEL)
    return uniqueModels.map((model) => ({
      ...model,
      hiddenByDefault: shouldHideByDefault(model.model_name, hasRecommendedOrion),
    }))
  }, [catalog])

  const filteredModels = useMemo(() => {
    const term = query.trim().toLowerCase()
    const exactAdvancedSearch = term.startsWith('stationzero-orion-route-') || term === 'stationzero-orion'
    let list = displayModels
    if (variant === 'add') list = list.filter((m) => !selectedSet.has(m.model_name))
    if (!showAdvanced && !exactAdvancedSearch) list = list.filter((m) => !m.hiddenByDefault)
    list = list.filter((m) => searchMatches(m, term))
    return sortModels(list, term)
  }, [displayModels, selectedSet, query, showAdvanced, variant])

  const hiddenCount = useMemo(() => {
    let list = displayModels
    if (variant === 'add') list = list.filter((m) => !selectedSet.has(m.model_name))
    return list.filter((m) => m.hiddenByDefault).length
  }, [displayModels, selectedSet, variant])

  const duplicateCount = displayModels.reduce((total, model) => total + Math.max(0, model.duplicateCount - 1), 0)

  const triggerLabel = label ?? (variant === 'replace' ? 'Selecionar modelo' : 'Adicionar modelo')

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled || catalog.length === 0}
          className={cn(
            buttonVariants({ variant: variant === 'add' ? 'outline' : 'outline', size: 'sm' }),
            'h-9 gap-2 font-normal',
            variant === 'add' && 'border-dashed text-muted-foreground hover:text-foreground',
            triggerClassName,
          )}
        >
          {variant === 'add' ? <PlusIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />}
          {triggerLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={6}
        className="w-[--popover-width] min-w-[min(360px,calc(100vw-2rem))] max-w-[min(640px,calc(100vw-2rem))] overflow-hidden rounded-lg border-border p-0 shadow-lg"
        style={contentWidth ? { '--popover-width': `${Math.max(contentWidth, 360)}px` } as React.CSSProperties : undefined}
      >
        <div className="border-b border-border bg-background">
          <div className="flex items-center gap-2 px-3 py-2">
            <SearchIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <Input
              ref={inputRef}
              type="text"
              value={query}
              placeholder="Buscar modelo, provider ou modo"
              onChange={(event) => setQuery(event.target.value)}
              className="h-8 border-0 px-0 text-sm shadow-none focus-visible:ring-0"
            />
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-border/70 px-3 py-2 text-[11px] text-muted-foreground">
            <span>
              {displayModels.length} modelos
              {duplicateCount > 0 && `, ${duplicateCount} duplicados ocultos`}
            </span>
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAdvanced((value) => !value)}
                aria-pressed={showAdvanced}
                className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 font-medium text-foreground hover:bg-accent"
              >
                <EyeIcon className="h-3 w-3" />
                {showAdvanced ? 'Ocultar aliases' : `Mostrar aliases (${hiddenCount})`}
              </button>
            )}
          </div>
        </div>

        <ScrollArea className="h-[min(420px,calc(100vh-14rem))]">
          <div className="p-1">
            {filteredModels.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                {catalog.length === 0
                  ? 'Catálogo indisponível.'
                  : query
                    ? 'Nenhum modelo com esse termo.'
                    : 'Todos os modelos já estão selecionados.'}
              </div>
            ) : (
              filteredModels.map((model) => {
                const isSelected = selectedSet.has(model.model_name)
                return (
                  <button
                    key={model.model_name}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onPick(model.model_name)
                      if (variant === 'replace') setOpen(false)
                    }}
                    className={cn(
                      'flex w-full flex-col items-start gap-1 rounded-md px-3 py-2.5 text-left transition-colors',
                      'hover:bg-accent focus:outline-none',
                      isSelected && variant === 'replace' && 'bg-accent',
                      model.model_name === RECOMMENDED_ORION_MODEL && 'bg-blue-50/80 ring-1 ring-blue-200 hover:bg-blue-50 dark:bg-blue-950/20 dark:ring-blue-900',
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="flex min-w-0 flex-1 items-baseline gap-2">
                        <span className="truncate font-mono text-sm text-foreground" title={model.model_name}>{model.model_name}</span>
                        <ModelProviderTag model={model} />
                        {model.model_name === RECOMMENDED_ORION_MODEL && (
                          <span className="rounded-sm bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                            Orion 2.0
                          </span>
                        )}
                        {model.hiddenByDefault && (
                          <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            alias
                          </span>
                        )}
                      </div>
                      {isSelected && variant === 'replace' && <CheckIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                    </div>
                    <ModelMetaBadges model={model} compact />
                  </button>
                )
              })
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}
